import React, { Fragment, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { CalendarMonth } from '@mui/icons-material';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';

const defaultValues = {
  display_name: '',
  description: '',
  is_active: true,
};

const validationSchema = Yup.object().shape({
  display_name: Yup.string(),
  description: Yup.string(),
  is_active: Yup.boolean().required(),
});

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Schools Calendars | Calendars ';
  }, []);
  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [appliedFilters, setAppliedFilters] = React.useState([]);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    calendars: [],
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    // clearErrors,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    setDataIsFetching(true);
    let requestUrl = `/school-calendars?per_page=${perPage}&page=${page}`;
    if (statusFilter === 'ACTIVE') {
      requestUrl += '&is_active=true';
      setAppliedFilters(['is_active']);
    } else if (statusFilter === 'INACTIVE') {
      requestUrl += '&is_active=false';
      setAppliedFilters(['is_active']);
    } else {
      setAppliedFilters([]);
    }

    if (statusFilter === 'ALL') {
      requestUrl = `/school-calendars?per_page=${perPage}&page=${page}`;
      setAppliedFilters([]);
    }

    userRequest()
      .get(requestUrl)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          calendars: data.schoolCalendars,
        });

        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.error(response);
        setDataIsFetching(false);
      });
  }, [statusFilter, perPage, page]);

  function getFilters() {
    if (statusFilter === 'ACTIVE') {
      return ['Status: Active'];
    } else if (statusFilter === 'INACTIVE') {
      return ['Status: Inactive'];
    } else {
      return [];
    }
  }

  function setStatus(id, data) {
    userRequest()
      .post(`/school-calendars/update/${id}`, data)
      .then(() => {
        let cals = tableData.calendars.map((cal) => {
          if (id === cal.id) cal.is_active = !cal.is_active;
          return cal;
        });
        if (appliedFilters.length > 0) {
          if (data.is_active !== statusFilter) cals = cals.filter((proj) => proj.id !== id);
        }
        setTableData({
          count: cals.length,
          calendars: cals,
        });
      })
      .catch((error) => {
        console.error('Error setting status:', error);
      });
  }

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(
        Object.keys(defaultValues).reduce((reduced, curr) => {
          reduced[curr] = row[curr];
          return reduced;
        }, {})
      );
      // clearErrors();
    },
    toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
    link: {
      icon: <CalendarMonth />,
      toolTip: 'View Terms',
      getLink: (row) => `/school-calendar-terms/manage?school_calendar_id=${row.id}`,
    },
  };

  const columns = [
    {
      field: 'display_name',
      headerName: 'Name',
      flex: 0.4,
    },

    {
      field: 'terms',
      headerName: 'Terms',
      type: 'number',
      flex: 0.6,
      align: 'left',
      headerAlign: 'left',

      cellClassName: ({ value }) => {
        // if (value === 0) return "text-muted";
        if (!value) return 'text-danger';
        else if (value < 4) return 'text-warning';
        else return 'text-primary';
      },
    },
    {
      field: 'active',
      headerName: 'Status',
      headerAlign: 'center',
      flex: 1,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  const onSubmit = (data) => {
    // console.log("Form Data:", data);
    if (data) {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });

      let api = modalState.update ? `/school-calendars/update/${modalState.update}` : '/school-calendars';
      let { display_name, description, is_active } = data;

      // Check if it's an update or create
      if (modalState.update) {
        userRequest()
          .post(api, {
            display_name,
            description,
            is_active,
          })
          .then((res) => {
            console.log('Update Response:', res.data);
            toast.dismiss('create');
            toast.success(`Successfully updated`, {
              position: 'bottom-center',
              autoClose: 5000,
              closeOnClick: true,
              draggable: true,
            });
            setTableData((prev) => {
              let calendars = [...prev.calendars];
              calendars = calendars.map((calendar) =>
                calendar.id === modalState.update ? res.data.schoolCalendar : calendar
              );
              return { count: prev.count, calendars };
            });
            setModalState({ update: true, open: false });
            reset(defaultValues, {});
          })
          .catch(({ response }) => {
            console.error('Error submitting data:', response);
            toast.dismiss('create');
            toast.error(response.data.message, {
              position: 'bottom-center',
              autoClose: 5000,
              closeOnClick: true,
              draggable: true,
            });
          });
      } else {
        // check exists and remove duplicates
        userRequest()
          .get(`/school-calendars?display_name=${display_name}`)
          .then((res) => {
            if (res.data.length > 0) {
              toast.dismiss('create');
              toast.error('School Calendar with this name already exists', {
                position: 'bottom-center',
                autoClose: 5000,
                closeOnClick: true,
                draggable: true,
              });
            } else {
              userRequest()
                .post(api, {
                  display_name,
                  description,
                  is_active,
                })
                .then((res) => {
                  console.log('Create Response:', res.data);
                  toast.dismiss('create');
                  toast.success(`Successfully created`, {
                    position: 'bottom-center',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                  });
                  setTableData((prev) => {
                    const calendars = [...prev.calendars, res.data.schoolCalendar];
                    return { count: prev.count + 1, calendars };
                  });
                  setModalState({ update: true, open: false });
                  reset(defaultValues, {});
                })
                .catch(({ response }) => {
                  console.error('Error submitting data:', response);
                  toast.dismiss('create');
                  toast.error(response.data.message, {
                    position: 'bottom-center',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                  });
                });
            }
          })
          .catch((error) => {
            console.error('Error checking display_name:', error);
            toast.dismiss('create');
            toast.error('Error checking School Calendar name', {
              position: 'bottom-center',
              autoClose: 5000,
              closeOnClick: true,
              draggable: true,
            });
          });
      }
    }
  };

  // remember
  return (
    <Fragment>
      <Breadcrumb icon={<CalendarMonth />} title="Manage" parent="School Calendars" />
      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <div className="form-group row mx-0">
            <FormControl>
              <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
              <Select
                name="Status"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(() => {
                    return e.target.value;
                  })
                }
                labelId="demo-simple-select-helper-label"
                id="demo-simple-select-helper"
                label="Status"
                style={{
                  fontStyle: statusFilter === 'All' ? 'italic' : 'normal',
                }}
              >
                <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                  All
                </MenuItem>
                <MenuItem value={'ACTIVE'}>Active</MenuItem>
                <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          setModalState({ open: true, update: false });
          reset(defaultValues, {});
          // clearErrors();
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ open: false, update: false });
          reset(defaultValues, {});
        }}
        size="lg"
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ open: false, update: false });
            reset(defaultValues, {});
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} School Calendar
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row">
                <TextField
                  label="Name"
                  variant="outlined"
                  {...register(`display_name`)}
                  error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="form-group row">
                <TextField label="Description" multiline rows={4} {...register(`description`)} />
              </div>
              <div className="d-flex justify-content-start my-4">
                {modalState.update && (
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                )}
              </div>
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.calendars}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

export default Manage;
